import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function CookiesPage() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="cookies-page">
      <Header />

      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-8 font-['Outfit']">Cookie Policy</h1>
          <p className="text-slate-600 mb-8">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">1. What Are Cookies?</h2>
              <p className="text-slate-600 leading-relaxed">
                Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners. Cookies help us remember your preferences and understand how you use our website.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">2. How We Use Cookies</h2>
              <p className="text-slate-600 leading-relaxed mb-4">QuickFinance.io uses cookies for the following purposes:</p>
              
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Essential Cookies</h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                These cookies are necessary for the website to function properly. They enable core functionality such as security, session management, and accessibility. You cannot opt out of these cookies.
              </p>
              
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Performance Cookies</h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve the website's performance and user experience.
              </p>
              
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Functional Cookies</h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                These cookies allow the website to remember choices you make (such as your preferred language or region) and provide enhanced, personalised features.
              </p>
              
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Marketing Cookies</h3>
              <p className="text-slate-600 leading-relaxed">
                These cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user. We only use these with your consent.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">3. Cookies We Use</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-slate-200 rounded-lg overflow-hidden">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Cookie Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Purpose</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Duration</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Type</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    <tr>
                      <td className="px-4 py-3 text-sm text-slate-600">session_id</td>
                      <td className="px-4 py-3 text-sm text-slate-600">Maintains user session</td>
                      <td className="px-4 py-3 text-sm text-slate-600">Session</td>
                      <td className="px-4 py-3 text-sm text-slate-600">Essential</td>
                    </tr>
                    <tr className="bg-slate-50/50">
                      <td className="px-4 py-3 text-sm text-slate-600">access_token</td>
                      <td className="px-4 py-3 text-sm text-slate-600">Authentication</td>
                      <td className="px-4 py-3 text-sm text-slate-600">1 hour</td>
                      <td className="px-4 py-3 text-sm text-slate-600">Essential</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-slate-600">refresh_token</td>
                      <td className="px-4 py-3 text-sm text-slate-600">Refresh authentication</td>
                      <td className="px-4 py-3 text-sm text-slate-600">7 days</td>
                      <td className="px-4 py-3 text-sm text-slate-600">Essential</td>
                    </tr>
                    <tr className="bg-slate-50/50">
                      <td className="px-4 py-3 text-sm text-slate-600">_ga</td>
                      <td className="px-4 py-3 text-sm text-slate-600">Google Analytics</td>
                      <td className="px-4 py-3 text-sm text-slate-600">2 years</td>
                      <td className="px-4 py-3 text-sm text-slate-600">Performance</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-slate-600">cookie_consent</td>
                      <td className="px-4 py-3 text-sm text-slate-600">Stores cookie preferences</td>
                      <td className="px-4 py-3 text-sm text-slate-600">1 year</td>
                      <td className="px-4 py-3 text-sm text-slate-600">Functional</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">4. Third-Party Cookies</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                We may use third-party services that set their own cookies. These include:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Google Analytics:</strong> To analyse website traffic and user behaviour</li>
                <li><strong>Google Tag Manager:</strong> To manage tracking scripts</li>
                <li><strong>Facebook Pixel:</strong> For advertising (with consent)</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                These third parties have their own privacy policies that govern their use of cookies.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">5. Managing Cookies</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                You can control and manage cookies in several ways:
              </p>
              
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Browser Settings</h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Most browsers allow you to refuse cookies or delete existing cookies. The methods for doing so vary from browser to browser:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies</li>
                <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                <li><strong>Edge:</strong> Settings → Privacy & Security → Cookies</li>
              </ul>
              
              <p className="text-slate-600 leading-relaxed">
                Please note that disabling cookies may affect the functionality of our website and prevent you from using certain features.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">6. Your Consent</h2>
              <p className="text-slate-600 leading-relaxed">
                When you first visit our website, you will be asked to consent to our use of cookies. You can change your preferences at any time by clearing your browser cookies and revisiting the site, or by contacting us directly.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">7. Changes to This Policy</h2>
              <p className="text-slate-600 leading-relaxed">
                We may update this Cookie Policy from time to time. Any changes will be posted on this page with an updated "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">8. Contact Us</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                If you have any questions about our use of cookies, please contact us:
              </p>
              <div className="bg-slate-50 rounded-xl p-6">
                <p className="text-slate-700"><strong>QuickFinance.io</strong></p>
                <p className="text-slate-600">Email: privacy@quickfinance.io</p>
                <p className="text-slate-600">Email: hello@quickfinance.io</p>
                <p className="text-slate-600">Address: 123 Finance Street, London, EC1A 1BB</p>
              </div>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
